import React, { useEffect, useState } from 'react';
import { DashboardShell } from 'components/DashboardShell';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import api, { getApiErrorMessage } from 'lib/api';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  Users, Package, Plane, ClipboardList, AlertTriangle, BadgeCheck,
  TrendingUp, DollarSign,
} from 'lucide-react';
import { formatPrice } from 'lib/format';
import { useLanguage } from 'lib/i18n';

export default function AdminDashboardPage() {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get('/admin/stats');
      setStats(r.data);
    } catch (e) {
      setError(getApiErrorMessage(e, t('admin.error.loadStats')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <DashboardShell><div>{t('admin.loading')}</div></DashboardShell>;

  if (error) {
    return (
      <DashboardShell>
        <div className="space-y-4">
          <p className="text-destructive">{error}</p>
          <Button onClick={load}>{t('admin.retry')}</Button>
        </div>
      </DashboardShell>
    );
  }

  if (!stats) return <DashboardShell><div>{t('admin.noData')}</div></DashboardShell>;

  const userData = [
    { name: t('admin.dashboard.senders'), value: stats.total_senders },
    { name: t('admin.dashboard.travelers'), value: stats.total_travelers },
    { name: t('admin.dashboard.couriers'), value: stats.total_deliverers },
  ];
  const missionData = [
    { name: t('admin.dashboard.inProgress'), value: stats.in_progress_missions },
    { name: t('admin.dashboard.delivered'), value: stats.delivered_missions },
  ];

  const COLORS = ['#ea580c', '#1e3a8a', '#16a34a'];

  return (
    <DashboardShell>
      <h1 className="text-2xl sm:text-3xl font-bold mb-6">{t('admin.dashboard.title')}</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard icon={Users} label={t('admin.dashboard.users')} value={stats.total_users} testid="admin-stats-total-users" />
        <StatCard icon={Package} label={t('admin.dashboard.totalParcels')} value={stats.total_deliveries} testid="admin-stats-total-deliveries" />
        <StatCard icon={Plane} label={t('admin.dashboard.totalTrips')} value={stats.total_trips} testid="admin-stats-total-trips" />
        <StatCard icon={ClipboardList} label={t('admin.dashboard.deliveredMissions')} value={stats.delivered_missions} testid="admin-stats-total-missions" />
        <StatCard icon={BadgeCheck} label={t('admin.dashboard.validatedCouriers')} value={stats.validated_deliverers} testid="admin-stats-validated" />
        <StatCard icon={AlertTriangle} label={t('admin.dashboard.openDisputes')} value={stats.open_disputes} testid="admin-stats-disputes" />
        <StatCard icon={TrendingUp} label={t('admin.dashboard.activeMissions')} value={stats.in_progress_missions} testid="admin-stats-active" />
        <StatCard icon={DollarSign} label={t('admin.dashboard.totalRevenue')} value={formatPrice(stats.total_revenue)} testid="admin-stats-revenue" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">{t('admin.dashboard.userDistribution')}</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={userData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({name, value}) => `${name}: ${value}`}>
                  {userData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">{t('admin.dashboard.missionsByStatus')}</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={missionData}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#ea580c" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </DashboardShell>
  );
}

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-muted-foreground">{label}</span>
        <Icon size={16} className="text-muted-foreground" />
      </div>
      <div className="text-2xl font-bold" data-testid={testid}>{value}</div>
    </CardContent>
  </Card>
);
